import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Zoom,
  Stack,
  styled
} from "@mui/material";

/**
 * ActionDialog - Componente Unificato per Conferme e Motivazioni
 */
const UnifiedDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '40px',
    padding: theme.spacing(3),
    boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
  }
}));

const ActionDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  actionType, // Es. "Sospendi", "Rifiuta", "Aggiornamento"
  vendorName,
  actionSummary, // React node per riepilogo (es. "Nuovo Stato: Attivo")
  reason,
  onReasonChange,
  emailChecked,
  onEmailChange,
  includeReasonChecked,
  onIncludeReasonChange,
  confirmText = "Conferma",
  confirmColor = "#0b3d87", // Colore dinamico per azioni critiche
  cancelText = "Annulla",
  showReasonField = true,
  showEmailCheckbox = true
}) => {
  return (
    <UnifiedDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slots={{ transition: Zoom }}
    >
      <DialogTitle sx={{
        color: confirmColor,
        fontWeight: 800,
        textAlign: 'center',
        fontSize: '1.6rem',
        pb: 1
      }}>
        {title}
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#4a5568' }}>
          {actionType ? (
            <>Sei sicuro di voler <strong>{actionType.toLowerCase()}</strong> <strong>{vendorName}</strong>?</>
          ) : (
            <>Stai per aggiornare i dati di <strong>{vendorName}</strong>.</>
          )}
        </Typography>

        {actionSummary && (
          <Box sx={{
            backgroundColor: "#f0f7ff",
            p: 2,
            borderRadius: 4,
            mb: 3,
            border: '1px solid #d0e2ff',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0b3d87' }}>
              {actionSummary}
            </Typography>
          </Box>
        )}

        {showReasonField && (
          <TextField
            label={"Motivazione / Ragione (Obbligatoria)"}
            multiline
            rows={4}
            fullWidth
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="Inserisci qui il motivo dell'azione..."
            variant="outlined"
            required={true}
            error={showReasonField && !reason?.trim()}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 4,
              }
            }}
          />
        )}

        {showEmailCheckbox && (
          <Stack spacing={1} sx={{
            backgroundColor: "#f8fafc",
            p: 1.5,
            px: 2,
            borderRadius: 4,
            border: '1px solid #e2e8f0'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailChecked}
                  onChange={(e) => onEmailChange(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Invia Email di notifica al venditore
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeReasonChecked}
                  onChange={(e) => onIncludeReasonChange(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Includi la ragione nella email
                </Typography>
              }
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 4, pt: 2, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            px: 4,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 'bold',
            color: '#718096'
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={showReasonField && !reason?.trim()}
          variant="contained"
          sx={{
            backgroundColor: confirmColor,
            px: 6,
            py: 1.2,
            borderRadius: 4,
            textTransform: 'none',
            fontWeight: 'bold',
            fontSize: '1rem',
            boxShadow: `0 8px 20px ${confirmColor}40`,
            '&:hover': { backgroundColor: confirmColor, filter: 'brightness(1.1)' },
            '&:disabled': { backgroundColor: '#e2e8f0', color: '#94a3b8' }
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </UnifiedDialog>
  );
};

ActionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  actionType: PropTypes.string,
  vendorName: PropTypes.string,
  actionSummary: PropTypes.node,
  reason: PropTypes.string,
  onReasonChange: PropTypes.func,
  emailChecked: PropTypes.bool,
  onEmailChange: PropTypes.func,
  includeReasonChecked: PropTypes.bool,
  onIncludeReasonChange: PropTypes.func,
  confirmText: PropTypes.string,
  confirmColor: PropTypes.string,
  cancelText: PropTypes.string,
  showReasonField: PropTypes.bool,
  showEmailCheckbox: PropTypes.bool
};

export default ActionDialog;
 